// HVT/TST-prioritering + alarm. Genererer PRIORITERTE varsler fra situasjonsbildet
// slik at befal ikke drukner i spor: høyverdimål (HVT) og tidskritiske mål (TST)
// fra mållista, fiendtlig nærhet til egne/beskyttede punkter, og hurtigmål.
// Rene regel-evaluatorer + prioritering + demping (dedup i tidsvindu). ML kan
// senere mate inn flere kandidat-varsler i samme kø.
import { z } from 'zod';
import { haversineM } from './geo.js';
export const AlertSeverity = z.enum(['info', 'advarsel', 'kritisk']);
const SEVERITY_RANK = { kritisk: 0, advarsel: 1, info: 2 };
/**
 * Alvorlighet → militær presedens. ÉN oversettelse (§3), brukt av
 * hendelsesloggens hastegrads-stripe (UX-funn F8).
 *
 * ⚠ INGEN VARSEL BLIR `flash`. FLASH er høyeste presedens i NATO-meldingstjeneste
 * og skal reserveres for det et MENNESKE erklærer som slikt. Disse varslene er
 * REGELUTLØSTE (`targetPriorityAlerts`, nærhet, hurtigmål, avvik, nøkkelord) —
 * en terskel som slo inn, ikke en vurdering noen har gjort. Å la en regel utløse
 * FLASH ville gjort det høyeste nivået til det vanligste, og da er det verdiløst
 * den dagen noen faktisk trenger det.
 */
export const ALERT_PRECEDENCE = {
    kritisk: 'immediate',
    advarsel: 'priority',
    info: 'routine',
};
// ── Regel: mållista (HVT/TST) ───────────────────────────────────────────────
/** Aktive TST → kritisk, HPT → advarsel. Terminale mål gir ingen alarm. */
export function targetPriorityAlerts(targets) {
    const out = [];
    for (const t of targets) {
        if (t.status === 'end-of-mission' || t.status === 'cancelled')
            continue;
        if (t.priority === 'tst') {
            out.push({ key: `tst:${t.id}`, kind: 'tst', severity: 'kritisk', refId: t.id, tsMs: t.tsMs,
                title: `TST: ${t.number}`, detail: `Tidskritisk mål «${t.desc}» — status ${t.status}` });
        }
        else if (t.priority === 'hpt') {
            out.push({ key: `hvt:${t.id}`, kind: 'hvt', severity: 'advarsel', refId: t.id, tsMs: t.tsMs,
                title: `HVT: ${t.number}`, detail: `Høyverdimål «${t.desc}» — status ${t.status}` });
        }
    }
    return out;
}
/**
 * Fiendtlig spor innenfor `radiusM` av et egne/beskyttet punkt → alarm.
 * Kritisk innenfor halve radiusen, ellers advarsel. Konfidens filtrerer støy.
 */
export function proximityAlerts(tracks, guarded, radiusM, minConfidence = 0.3) {
    const out = [];
    for (const tr of tracks) {
        if (tr.affiliation !== 'hostile')
            continue;
        if ((tr.confidence ?? 1) < minConfidence)
            continue;
        const p = { lat: tr.lat, lon: tr.lon };
        let nearest = null;
        for (const g of guarded) {
            const d = haversineM(p, { lat: g.lat, lon: g.lon });
            if (d <= radiusM && (!nearest || d < nearest.d))
                nearest = { pt: g, d };
        }
        if (nearest) {
            const severity = nearest.d <= radiusM / 2 ? 'kritisk' : 'advarsel';
            out.push({ key: `prox:${tr.id}:${nearest.pt.id}`, kind: 'proximity', severity, refId: tr.id, tsMs: tr.tsMs,
                title: `Fiende nær ${nearest.pt.label}`, detail: `${tr.label} ${Math.round(nearest.d)} m fra ${nearest.pt.label}` });
        }
    }
    return out;
}
// ── Regel: hurtigmål (fast mover) ───────────────────────────────────────────
/** Fiendtlig/ukjent spor med fart over terskel (m/s) → advarsel (fly/rakett/drone). */
export function fastMoverAlerts(speeds, thresholdMps = 100) {
    const out = [];
    for (const { track, speedMps } of speeds) {
        if (track.affiliation === 'friend')
            continue;
        if (speedMps < thresholdMps)
            continue;
        out.push({ key: `fast:${track.id}`, kind: 'fast-mover', severity: 'advarsel', refId: track.id, tsMs: track.tsMs,
            title: `Hurtigmål: ${track.label}`, detail: `${Math.round(speedMps)} m/s (${Math.round(speedMps * 3.6)} km/t)` });
    }
    return out;
}
// ── Regel: ny sensorobservasjon (feltbilde, kamera, peiling) ────────────────
/**
 * Hvor lenge en observasjon regnes som «ny» og skal varsles + blinke.
 *
 * 90 s er valgt slik at en operatør som ser bort et øyeblikk fortsatt fanger
 * varselet, men et spor som har ligget i kartet en stund ikke blinker på nytt
 * hver gang hubben sender et snapshot.
 */
export const SENSOR_ALERT_VINDU_MS = 90_000;
/**
 * Varsle om FERSKE retningsbestemte observasjoner.
 *
 * Eierkravet (2026-08-17): *«når det kommer viktig info så skal de sensorene som
 * detekterer noe av interesse blinke opp i noen sekunder og med en advarsel som
 * lar operatører velge å rette oppmerksomheten mot denne.»*
 *
 * ⚠ DEFAULT-ALVORLIGHET ER `info`, IKKE `advarsel` — OG DET ER VIKTIG.
 * (En kilde kan overstyre via `Track.varselAlvor`; Frigate gjør det, fordi den
 * lokale NVR-oppsetningen kjenner soner og filtre hubben ikke kjenner.)
 * Et feltbilde er en OBSERVASJON, ikke en trusselvurdering. Ingen har sagt at det
 * som er fotografert er fiendtlig. Ble dette `advarsel`, ville hver rutinemessige
 * dokumentasjon konkurrert visuelt med fiendtlig nærhet og hurtigmål, og etter en
 * halvtimes patrulje ville operatøren sluttet å se på varselkøen i det hele tatt.
 * Alarmtretthet er ikke et komfortproblem; det er hvordan et reelt varsel går
 * tapt. Er observasjonen faktisk en trussel, er det de ANDRE reglene
 * (proximity/HVT/TST) som skal rope.
 *
 * ⚠ FILTRERER PÅ `siktsektor`, IKKE PÅ KILDE. Da gjelder regelen automatisk for
 * Frigate-deteksjoner, PTZ-kameraer og peileantenner når de wires — de er samme
 * slags påstand («en sensor så noe i denne retningen»), og en kildeliste her
 * ville krevd en kodeendring per ny sensortype.
 */
export function sensorObservasjonAlerts(tracks, naa, vinduMs = SENSOR_ALERT_VINDU_MS) {
    const out = [];
    for (const t of tracks) {
        if (!t.siktsektor)
            continue;
        // Framtidige tidsstempler (klokkeskeiv telefon) skal ikke blinke i det
        // uendelige: alderen måles absolutt.
        const alder = Math.abs(naa - t.tsMs);
        if (t.tsMs === 0 || alder > vinduMs)
            continue;
        const bearing = Math.round(t.siktsektor.bearingDeg);
        const retning = `${bearing}°`;
        // Kildens egen vurdering vinner over vår default — se `Track.varselAlvor`.
        const severity = t.varselAlvor ?? 'info';
        const rekkevidde = t.extra?.['Rekkevidde'];
        out.push({
            key: `sensor:${t.id}`,
            kind: 'sensor',
            severity,
            refId: t.id,
            tsMs: t.tsMs,
            title: t.label,
            detail: rekkevidde
                ? `Observasjon mot ${retning} — ${rekkevidde}`
                : `Observasjon mot ${retning}`,
            bearingDeg: bearing,
            // SENS-33: sensorvarsler leses opp (talegruppens «demo-kjede») og skal
            // derfor ikke kunne skrolle forbi ukvittert.
            krevKvittering: true,
        });
    }
    return out;
}
// ── Regel: deteksjon UTEN posisjon (ukalibrert kilde, VALE SENS-21) ─────────
/**
 * Varsle om FERSKE deteksjoner en kilde IKKE kan posisjonere (i dag: et
 * Frigate-kamera uten `Kamerakalibrering`, se `frigateTilSporUtenPosisjon`).
 *
 * Uten dette faller en slik deteksjon stille bort: `Track` krever lat/lon, så
 * det finnes ikke et spor å bygge et kart-varsel fra, og operatøren får aldri
 * vite at NOE ble sett. Deteksjonen sto tidligere KUN i revisjonsloggen og i
 * HTTP-svaret fra ingest-endepunktet — usynlig i COP. Denne regelen er den
 * ærlige, minimale fiksen: et varsel i køen, tydelig merket som uposisjonert,
 * IKKE et kartpunkt (det ville vært oppdiktet presisjon — se `SporUtenPosisjon`
 * sin filhode-kommentar for hvorfor `usikkerhet` her er tom).
 *
 * ⚠ INGEN `bearingDeg`. `sensorObservasjonAlerts` leser `Track.siktsektor` for
 * en retning; en uposisjonert kilde har per definisjon ingen — å late som den
 * hadde en ville vært akkurat den slags oppdiktede presisjon §1 forbyr.
 *
 * Samme demings-idiom som `sensorObservasjonAlerts`: ferskt vindu, kildens
 * egen `varselAlvor` vinner over default `info` (se samme begrunnelse der —
 * en uposisjonert deteksjon er en OBSERVASJON, ikke en trusselvurdering).
 */
export function uposisjonertKontaktAlerts(spor, naa, vinduMs = SENSOR_ALERT_VINDU_MS) {
    const out = [];
    for (const s of spor) {
        const alder = Math.abs(naa - s.tsMs);
        if (s.tsMs === 0 || alder > vinduMs)
            continue;
        out.push({
            key: `uposisjonert:${s.id}`,
            kind: 'uposisjonert',
            severity: s.varselAlvor ?? 'info',
            refId: s.id,
            tsMs: s.tsMs,
            title: s.label,
            detail: `⚠ UKALIBRERT/UPOSISJONERT — ${s.begrunnelse}`,
            // SENS-33-idiomet: en deteksjon uten posisjon skal ikke kunne skrolle
            // forbi ukvittert heller — operatøren skal aktivt ha SETT at noe ble
            // detektert, selv uten kartpunkt.
            krevKvittering: true,
        });
    }
    return out;
}
// ── Opplesing + kvittering (SENS-33: sensor-til-tale) ───────────────────────
//
// Eierens prioritet #5, demonstrasjonen av HELE VALE-kjeden ende-til-ende:
// sensordeteksjon → varsel → opplest i talegruppa → kvittering. `speak()`
// (nb-NO TTS) og varselkøen (`sensorObservasjonAlerts` + blink) fantes alt —
// dette er ledningen mellom dem.
//
// ⚠ ÆRLIG OM «TALEGRUPPA»: `speak()` (ptt.ts) er nettbleserens lokale
// SpeechSynthesis — den leser opp PÅ OPERATØRENS EGEN maskin, akkurat som en
// innkommende PTT-tekstmelding leses opp lokalt i dag (`main.ts`, linje ~1507:
// `if (incoming && ptt.ttsEnabled) speak(...)`). Det finnes INGEN lyd-transport
// i kodebasen (ingen WebRTC/MediaStream) som sender denne talen ut til andre
// operatørers talegrupper, og INGEN kobling fra et varsel til en `kanalId`
// (kanaloversikten er operatørens EGEN vaktliste, ikke et sensor→kanal-kart).
// Denne funksjonen leser altså varselet opp for DENNE operatøren, i den
// forstand at hun kan repetere det muntlig i sin egen talegruppe — den sender
// ikke tale over sambandet. Se rapporten i PR-en for detaljer.
/**
 * Sett sammen en kort norsk tale-setning for `speak()`.
 *
 * ⚠ BARE FELT SOM FINNES PÅ VARSELET (§1 — ingen oppdiktet presisjon). Ingen
 * sektornummer (det finnes ikke i datamodellen); peiling i grader er det vi
 * faktisk har, og det er det som leses.
 */
export function alertTale(a) {
    const peiling = a.bearingDeg !== undefined ? `, peiling ${a.bearingDeg} grader` : '';
    const kvittering = a.krevKvittering ? ' Kvittering kreves.' : '';
    return `Varsel: ${a.title}${peiling}.${kvittering}`;
}
/**
 * Les FERSKE sensorvarsler opp via injisert `speak` (ren funksjon — ingen
 * DOM/Web Speech-avhengighet her, den bor i `ptt.ts`; kalleren styrer om TTS er
 * på, f.eks. `ptt.ttsEnabled`).
 *
 * ⚠ BARE `kind: 'sensor'` LESES OPP. HVT/TST, nærhet og hurtigmål har alt
 * sterkere uttrykk (kart, farge). Leste vi opp ALT, ville talen druknet
 * operatøren i nettopp det travle øyeblikket varselkøen finnes for å hjelpe med.
 */
export function announceNewSensorAlerts(fresh, speak) {
    for (const a of fresh) {
        if (a.kind === 'sensor')
            speak(alertTale(a));
    }
}
/**
 * Kvitter et varsel: sett `kvittertAv`/`kvittertMs`. Immutabelt (ny liste ut) —
 * samme idiom som `settTx`/`fjernVakt` i `pttvakt.ts`, aldri muter kalleren.
 * Varsler som ikke krever kvittering, eller som alt er kvittert, er urørt.
 */
export function kvitterAlert(alerts, key, bruker, tsMs) {
    return alerts.map((a) => (a.key === key && a.krevKvittering && a.kvittertMs === undefined
        ? { ...a, kvittertAv: bruker, kvittertMs: tsMs }
        : a));
}
// ── Prioritering + demping ──────────────────────────────────────────────────
/** Sorter kritisk → advarsel → info, nyeste først innen nivå. */
export function prioritizeAlerts(alerts) {
    return [...alerts].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.tsMs - a.tsMs);
}
/**
 * Demp gjentakelser: dropp et varsel hvis samme `key` er sett innenfor
 * `windowMs`. `seen` (key→sist-tsMs) muteres og kan gjenbrukes mellom kall.
 */
export function dedupeAlerts(alerts, seen, windowMs = 60_000) {
    const fresh = [];
    for (const a of alerts) {
        const last = seen.get(a.key);
        if (last !== undefined && a.tsMs - last < windowMs)
            continue;
        seen.set(a.key, a.tsMs);
        fresh.push(a);
    }
    return fresh;
}
//# sourceMappingURL=alerts.js.map